import React, { useState } from 'react';
import { MicIcon, SquareIcon, PenLineIcon, FileUpIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
type InputMode = 'voice' | 'text' | 'upload';
export function VoiceDescriptionScreen({
  data,
  updateData,
  onNext
}: ScreenProps) {
  const [mode, setMode] = useState<InputMode>('voice');
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecorded, setHasRecorded] = useState(data.taskDescription.length > 0);
  const [transcription, setTranscription] = useState(data.taskDescription);
  const [textInput, setTextInput] = useState('');
  const handleRecordToggle = () => {
    if (isRecording) {
      setIsRecording(false);
      setHasRecorded(true);
      const text = 'we lifting steel beams with crane to second floor structure this morning';
      setTranscription(text);
      updateData({
        taskDescription: text
      });
    } else {
      setIsRecording(true);
      setTranscription('');
      setHasRecorded(false);
      setTimeout(() => {
        setIsRecording(false);
        setHasRecorded(true);
        const text = 'we lifting steel beams with crane to second floor structure this morning';
        setTranscription(text);
        updateData({
          taskDescription: text
        });
      }, 3000);
    }
  };
  const modes: {
    id: InputMode;
    label: string;
    icon: typeof MicIcon;
  }[] = [{
    id: 'voice',
    label: 'Voice',
    icon: MicIcon
  }, {
    id: 'text',
    label: 'Type',
    icon: PenLineIcon
  }, {
    id: 'upload',
    label: 'Upload',
    icon: FileUpIcon
  }];
  return <div className="flex flex-col h-full" data-id="element-949">
      <div className="mb-5" data-id="element-950">
        <h2 className="text-2xl font-bold text-white mb-2" data-id="element-951">
          Describe the Work
        </h2>
        <p className="text-slate-400 text-sm" data-id="element-952">
          Briefly describe the task in your own words.
        </p>
      </div>

      {/* Mode Selector */}
      <div className="flex bg-slate-900 border border-slate-800 rounded-xl p-1 mb-6 relative" data-id="element-953">
        {modes.map(m => {
        const Icon = m.icon;
        const isActive = mode === m.id;
        return <button key={m.id} onClick={() => setMode(m.id)} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold transition-colors relative z-10 ${isActive ? 'text-white' : 'text-slate-500'}`} data-id="element-954">
              {isActive && <motion.div layoutId="mode-bg" className="absolute inset-0 bg-slate-800 rounded-lg" transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30
          }} data-id="element-955" />}
              <span className="relative z-10 flex items-center gap-1.5" data-id="element-956">
                <Icon className="w-3.5 h-3.5" data-id="element-957" />
                {m.label}
              </span>
            </button>;
      })}
      </div>

      {/* Voice Mode */}
      {mode === 'voice' && <div className="flex-1 flex flex-col items-center justify-center space-y-10" data-id="element-958">
          <div className="relative flex items-center justify-center" data-id="element-959">
            {isRecording && <motion.div animate={{
          scale: [1, 1.3, 1]
        }} transition={{
          repeat: Infinity,
          duration: 1.5
        }} className="absolute w-32 h-32 bg-orange-500/20 rounded-full" data-id="element-960" />}
            {isRecording && <motion.div animate={{
          scale: [1, 1.15, 1]
        }} transition={{
          repeat: Infinity,
          duration: 1.5,
          delay: 0.2
        }} className="absolute w-24 h-24 bg-orange-500/40 rounded-full" data-id="element-961" />}
            <motion.button whileTap={{
          scale: 0.9
        }} onClick={handleRecordToggle} className={`relative z-10 w-20 h-20 rounded-full flex items-center justify-center shadow-xl transition-colors ${isRecording ? 'bg-red-500 shadow-red-500/30' : 'bg-orange-500 shadow-orange-500/30'}`} data-id="element-962">
              {isRecording ? <SquareIcon className="w-8 h-8 text-white fill-current" data-id="element-963" /> : <MicIcon className="w-8 h-8 text-white" data-id="element-964" />}
            </motion.button>
          </div>

          <div className="text-center h-8" data-id="element-965">
            <p className={`font-medium text-sm ${isRecording ? 'text-red-400 animate-pulse' : 'text-slate-400'}`} data-id="element-966">
              {isRecording ? 'Recording...' : hasRecorded ? 'Tap to re-record' : 'Tap to start recording'}
            </p>
          </div>

          <div className={`w-full p-5 rounded-xl bg-slate-900 border border-slate-800 min-h-[100px] transition-opacity duration-500 ${hasRecorded ? 'opacity-100' : 'opacity-0'}`} data-id="element-967">
            <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-2" data-id="element-968">
              Transcription
            </p>
            <p className="text-slate-300 font-mono text-sm leading-relaxed" data-id="element-969">
              {transcription && `"${transcription}"`}
            </p>
          </div>
        </div>}

      {/* Text Mode */}
      {mode === 'text' && <div className="flex-1 flex flex-col space-y-4" data-id="element-970">
          <div className="flex-1 bg-slate-900 border border-slate-800 rounded-xl p-4 focus-within:border-orange-500/40 transition-colors" data-id="element-971">
            <textarea placeholder="Describe the work task, location, and key activities..." className="w-full h-full bg-transparent text-slate-300 text-sm placeholder-slate-600 resize-none outline-none leading-relaxed" value={textInput} onChange={e => {
          setTextInput(e.target.value);
          updateData({
            taskDescription: e.target.value
          });
        }} autoFocus data-id="element-972" />
          </div>
          <p className="text-slate-600 text-xs text-right" data-id="element-973">
            {textInput.length > 0 ? `${textInput.length} characters` : 'Start typing...'}
          </p>
        </div>}

      {/* Upload Mode (placeholder) */}
      {mode === 'upload' && <div className="flex-1 flex flex-col items-center justify-center space-y-4" data-id="element-974">
          <div className="w-full border-2 border-dashed border-slate-700 rounded-xl p-8 flex flex-col items-center justify-center text-center" data-id="element-975">
            <FileUpIcon className="w-10 h-10 text-slate-600 mb-3" data-id="element-976" />
            <p className="text-slate-400 text-sm font-medium mb-1" data-id="element-977">
              Upload a work document
            </p>
            <p className="text-slate-600 text-xs" data-id="element-978">
              PDF, Word, or image of a work order, scope, or method statement
            </p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 flex items-center gap-2 w-full" data-id="element-979">
            <FileUpIcon className="w-4 h-4 text-amber-500 flex-shrink-0" data-id="element-980" />
            <p className="text-amber-200 text-xs" data-id="element-981">
              Document upload is available in the full version.
            </p>
          </div>
        </div>}

      <div className="mt-auto pt-5" data-id="element-982">
        <PrimaryButton onClick={onNext} disabled={mode === 'voice' && !hasRecorded && !isRecording || mode === 'text' && textInput.trim().length === 0 || mode === 'upload'} data-id="element-983">
          Analyze Task
        </PrimaryButton>
      </div>
    </div>;
}